/**
 * Branchen und Startpakete.
 *
 * Das Prinzip in einem Satz: generisch bauen, branchenspezifisch befüllen.
 *
 * Die Branche ist ein Etikett am Mandanten und steuert genau eine Sache –
 * welches Startpaket beim Einrichten geladen wird. Sie steuert nicht das
 * Datenmodell, nicht die Rechte, nicht die Abläufe. Ein Malerbetrieb bekommt
 * dieselbe Software wie ein Parkettleger, nur mit anderem Inhalt.
 *
 * Das ist keine Geschmacksfrage. Sobald eine Abfrage "wenn Branche = Maler,
 * dann anders" enthält, gibt es zwei Produkte statt einem, und jede spätere
 * Änderung muss in beiden stimmen. Der Nutzen der Branche liegt vollständig
 * in den ersten zehn Minuten eines neuen Betriebs: nicht vor einem leeren
 * Katalog zu stehen. Danach ist alles frei änderbar, und was einmal geändert
 * wurde, wird nie wieder von einem Paket überschrieben.
 *
 * Seit es eine Registrierung gibt, wählt der Betrieb seine Branche selbst –
 * beim Bestellen, in einem einzigen Feld. Was danach im Katalog steht, ist ein
 * Vorschlag und kein Bestand: jede Zeile darf geändert, gelöscht oder ersetzt
 * werden, und nichts davon wird je wieder überschrieben.
 */

export const INDUSTRIES = [
  /*
    Fünfzehn Gewerbe und ein Ausweg.

    Vorher standen hier sieben, und «Boden / Parkett» war eines davon – ein
    Plattenleger und ein Parkettleger in derselben Zeile. Das ist ungefähr so
    treffend wie «Elektriker und Sanitär»: beide arbeiten am Boden, und damit
    hört es auf. Der eine verlegt Holz und schleift, der andere schneidet
    Keramik und verfugt; keine einzige Katalogzeile ist dieselbe.

    Eine Branche zu wenig ist teurer als eine zu viel: wer sich nicht findet,
    wählt irgendetwas und löscht danach zwölf Zeilen, die nicht passen.
    Deshalb lieber fünfzehn – und «andere» für alles, was hier fehlt. Dort
    entsteht bewusst KEIN Katalog: ein erfundener ist schlimmer als ein
    leerer, weil ihn niemand mehr prüft.
  */
  'boden-parkett',
  'platten',
  'bodenbelag',
  'maler-gipser',
  'schreiner',
  'elektro',
  'sanitaer',
  'heizung-lueftung',
  'dach-spengler',
  'fenster-storen',
  'reinigung',
  'gartenbau',
  'bau-regie',
  'montage-service',
  'umzug-transport',
  /*
    Kinderbetreuung – die erste Branche, die kein Handwerk ist.

    Sie steht am Ende der Liste und nicht zwischen den Gewerben, weil die
    Reihenfolge die Auswahl im Bestellformular bestimmt: Ein Parkettleger soll
    nicht zwischen «Montage» und «Umzug» eine Kita suchen müssen.

    Am Datenmodell ändert sie nichts, und das ist die Probe darauf, dass die
    Branche wirklich nur ein Etikett ist. Eine Kita rechnet Betreuungstage statt
    Quadratmeter ab – also andere Katalogzeilen, andere Einheiten, derselbe
    Beleg, dieselbe QR-Rechnung, dieselbe Mandantentrennung.
  */
  'kinderbetreuung',
  'andere'
] as const

export type Industry = (typeof INDUSTRIES)[number]

export const INDUSTRY_LABELS: Record<Industry, string> = {
  'boden-parkett': 'Parkett und Holzböden',
  platten: 'Plattenleger und Keramik',
  bodenbelag: 'Bodenbeläge textil und elastisch',
  'maler-gipser': 'Maler und Gipser',
  schreiner: 'Schreiner und Innenausbau',
  elektro: 'Elektroinstallation',
  sanitaer: 'Sanitär',
  'heizung-lueftung': 'Heizung und Lüftung',
  'dach-spengler': 'Dach und Spengler',
  'fenster-storen': 'Fenster und Storen',
  reinigung: 'Reinigung',
  gartenbau: 'Garten- und Landschaftsbau',
  'bau-regie': 'Bau und Regiearbeiten',
  'montage-service': 'Montage und Service',
  'umzug-transport': 'Umzug und Transport',
  kinderbetreuung: 'Kinderbetreuung und Kita',
  andere: 'Keine davon passt'
}

export function isIndustry(value: unknown): value is Industry {
  return typeof value === 'string' && (INDUSTRIES as readonly string[]).includes(value)
}

/**
 * Ein Startpaket ist ein Vorschlag, kein Schema.
 *
 * Es füllt einen leeren Mandanten mit dem, was ein Betrieb dieser Branche
 * üblicherweise braucht. Jede einzelne Zeile darf danach geändert, gelöscht
 * oder ersetzt werden – deshalb trägt jede Position einen Schlüssel, damit
 * ein späterer Lauf erkennt, was aus dem Paket stammt und was von Hand kam.
 */
export interface StartPackageItem {
  code: string
  name: string
  unit: string
  /** In Rappen, wie überall. Ein Paket ohne Preise setzt 0. */
  unitPriceRp: number
  kind: 'arbeit' | 'material'
}

export interface StartPackage {
  key: string
  industry: Industry
  label: string
  /** Einheiten, die in dieser Branche üblich sind – für Auswahllisten. */
  units: string[]
  /**
   * Getrennt je Dokumentart – ein gemeinsamer Text führte dazu, dass auf
   * Rechnungen die Gültigkeitsdauer einer Offerte stand.
   */
  invoiceConditionsText: string
  quoteConditionsText: string
  items: StartPackageItem[]
}

/**
 * ---------------------------------------------------------------------------
 * Warum fast alle Preise auf null stehen
 * ---------------------------------------------------------------------------
 *
 * Weil ein erfundener Preis schlimmer ist als kein Preis.
 *
 * Die Bezeichnungen sind echtes Handwerk: «Wände streichen, zwei Anstriche»
 * heisst in jedem Malerbetrieb so, und die Einheit ist überall m². Das lässt
 * sich aufschreiben, ohne einen einzigen Betrieb gefragt zu haben.
 *
 * Ein Ansatz lässt sich das nicht. Er hängt an Region, Betriebsgrösse,
 * Auslastung und daran, was der Betrieb in den letzten zehn Jahren verlangt
 * hat. Eine Zahl hier hineinzuschreiben hiesse, dass sie irgendwann ohne
 * Prüfung auf einer echten Offerte landet – und der Betrieb merkt es, wenn er
 * den Auftrag verloren hat oder wenn er draufgezahlt hat.
 *
 * Null ist deshalb keine Lücke, sondern eine Aussage: die Zeile ist richtig,
 * der Preis gehört dir. Die Oberfläche sagt das auch so.
 *
 * Boden/Parkett ist die Ausnahme und trägt Preise, weil sie aus dem Bestand
 * eines echten Betriebs stammen und nicht aus einer Schätzung.
 */
/*
  Die Sätze, die in jedem Gewerbe gleich gelten.

  Sie stehen hier einmal und werden von den Paketen unten geteilt. Drei
  Abschriften desselben Satzes weichen mit Sicherheit irgendwann voneinander
  ab – und dann steht auf der Offerte eines Betriebs etwas anderes als auf
  seiner Rechnung.
*/
const ALLGEMEIN_RECHNUNG = [
  'Zahlbar innert 30 Tagen ab Rechnungsdatum.',
  'Zusatzarbeiten, die während der Ausführung bestellt werden, werden nach Aufwand verrechnet.'
].join('\n')

/*
  Für die Kinderbetreuung ein eigener Rechnungstext.

  Der allgemeine nennt «Zusatzarbeiten nach Aufwand» – ein Satz aus der
  Baustelle, der auf einer Betreuungsrechnung nichts zu suchen hat. Die
  Zahlungsfrist bleibt dieselbe: dreissig Tage ab Rechnungsdatum.

  Die Kündigungsfrist steht NICHT darin. Sie gehört in den Betreuungsvertrag
  und ist je Betrieb verschieden; ein Satz im Konditionstext aller Kitas wäre
  eine Zusage, die wir für sie machen.
*/
const ALLGEMEIN_KITA_RECHNUNG = [
  'Zahlbar innert 30 Tagen ab Rechnungsdatum.',
  'Die Monatspauschale gilt unabhängig von der Zahl der Betreuungstage im Kalendermonat.'
].join('\n')

const ALLGEMEIN_OFFERTE = [
  'Diese Offerte ist 30 Tage ab Ausstellungsdatum gültig.',
  'Zusatzarbeiten, die während der Ausführung bestellt werden, werden nach Aufwand verrechnet.'
].join('\n')

export const START_PACKAGES: StartPackage[] = [
  {
    key: 'boden-parkett-standard',
    industry: 'boden-parkett',
    label: 'Boden / Parkett – Grundkatalog',
    units: ['m²', 'lfm', 'Std.', 'Stk.', 'Psch.'],
    /*
      Die gemeinsamen Sätze stehen in beiden Texten – Zusatzarbeiten und
      bauseitige Voraussetzungen gelten für die Offerte wie für die Rechnung.
      Verschieden ist nur der erste Satz, und genau der ist der wichtige.
    */
    invoiceConditionsText: [
      'Zahlbar innert 30 Tagen ab Rechnungsdatum.',
      'Zusatzarbeiten, die während der Ausführung bestellt werden, werden nach Aufwand verrechnet.',
      'Bauseitige Voraussetzungen: Räume besenrein, beheizt und frei von Möbeln. Restfeuchte des Unterlagsbodens gemäss Norm.'
    ].join('\n'),
    quoteConditionsText: [
      'Diese Offerte ist 30 Tage ab Ausstellungsdatum gültig.',
      'Zusatzarbeiten, die während der Ausführung bestellt werden, werden nach Aufwand verrechnet.',
      'Bauseitige Voraussetzungen: Räume besenrein, beheizt und frei von Möbeln. Restfeuchte des Unterlagsbodens gemäss Norm.'
    ].join('\n'),
    items: [
      {
        code: '1010',
        name: 'Parkett verlegen, schwimmend',
        unit: 'm²',
        unitPriceRp: 4500,
        kind: 'arbeit'
      },
      {
        code: '1020',
        name: 'Parkett verlegen, vollflächig verklebt',
        unit: 'm²',
        unitPriceRp: 6800,
        kind: 'arbeit'
      },
      {
        code: '1100',
        name: 'Unterlagsboden schleifen',
        unit: 'm²',
        unitPriceRp: 1200,
        kind: 'arbeit'
      },
      {
        code: '1300',
        name: 'Sockelleisten montieren',
        unit: 'lfm',
        unitPriceRp: 1400,
        kind: 'arbeit'
      },
      {
        code: '9000',
        name: 'Regiearbeit Monteur',
        unit: 'Std.',
        unitPriceRp: 8500,
        kind: 'arbeit'
      },
      { code: '2000', name: 'Trittschalldämmung', unit: 'm²', unitPriceRp: 750, kind: 'material' },
      { code: '2900', name: 'Kleinmaterial', unit: 'Psch.', unitPriceRp: 0, kind: 'material' }
    ]
  },
  /*
    Ab hier: Bezeichnungen und Einheiten, keine Preise. Der Grund steht im
    Kommentar über dieser Liste.
  */
  {
    key: 'maler-gipser-standard',
    industry: 'maler-gipser',
    label: 'Maler / Gipser – Grundkatalog',
    units: ['m²', 'lfm', 'Std.', 'Stk.', 'Psch.'],
    invoiceConditionsText: ALLGEMEIN_RECHNUNG,
    quoteConditionsText: ALLGEMEIN_OFFERTE,
    items: [
      {
        code: '1010',
        name: 'Wände streichen, zwei Anstriche',
        unit: 'm²',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      {
        code: '1020',
        name: 'Decken streichen, zwei Anstriche',
        unit: 'm²',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      {
        code: '1030',
        name: 'Untergrund spachteln und schleifen',
        unit: 'm²',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '1040', name: 'Abdecken und Abkleben', unit: 'm²', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '1100',
        name: 'Gipserarbeiten, Wand verputzen',
        unit: 'm²',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '1110', name: 'Risse sanieren', unit: 'lfm', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '1200',
        name: 'Fenster- und Türrahmen streichen',
        unit: 'Stk.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '9000', name: 'Regiearbeit Maler', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '9100', name: 'Anfahrt und Wegzeit', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '2000',
        name: 'Dispersionsfarbe weiss',
        unit: 'Stk.',
        unitPriceRp: 0,
        kind: 'material'
      },
      { code: '2010', name: 'Grundierung', unit: 'Stk.', unitPriceRp: 0, kind: 'material' },
      { code: '2900', name: 'Kleinmaterial', unit: 'Psch.', unitPriceRp: 0, kind: 'material' }
    ]
  },
  {
    key: 'reinigung-standard',
    industry: 'reinigung',
    label: 'Reinigung – Grundkatalog',
    units: ['m²', 'Std.', 'Stk.', 'Psch.'],
    invoiceConditionsText: ALLGEMEIN_RECHNUNG,
    quoteConditionsText: ALLGEMEIN_OFFERTE,
    items: [
      { code: '1010', name: 'Unterhaltsreinigung', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '1020',
        name: 'Umzugsreinigung mit Abgabegarantie',
        unit: 'm²',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '1030', name: 'Baureinigung, grob', unit: 'm²', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1040', name: 'Baureinigung, fein', unit: 'm²', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '1100',
        name: 'Fensterreinigung beidseitig',
        unit: 'Stk.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      {
        code: '1110',
        name: 'Storen und Rollläden reinigen',
        unit: 'Stk.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '1200', name: 'Teppich nass reinigen', unit: 'm²', unitPriceRp: 0, kind: 'arbeit' },
      { code: '9000', name: 'Regiearbeit Reinigung', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '9100', name: 'Anfahrt und Wegzeit', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '2000', name: 'Reinigungsmittel', unit: 'Psch.', unitPriceRp: 0, kind: 'material' },
      { code: '2900', name: 'Entsorgung', unit: 'Psch.', unitPriceRp: 0, kind: 'material' }
    ]
  },
  {
    key: 'gartenbau-standard',
    industry: 'gartenbau',
    label: 'Gartenbau – Grundkatalog',
    units: ['m²', 'lfm', 'Std.', 'Stk.', 'm³', 'Psch.'],
    invoiceConditionsText: ALLGEMEIN_RECHNUNG,
    quoteConditionsText: ALLGEMEIN_OFFERTE,
    items: [
      { code: '1010', name: 'Rasen ansäen', unit: 'm²', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1020', name: 'Rollrasen verlegen', unit: 'm²', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1030', name: 'Hecke schneiden', unit: 'lfm', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '1040',
        name: 'Baum fällen und entsorgen',
        unit: 'Stk.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '1100', name: 'Plattenweg verlegen', unit: 'm²', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1110', name: 'Randabschluss setzen', unit: 'lfm', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1200', name: 'Aushub und Abtransport', unit: 'm³', unitPriceRp: 0, kind: 'arbeit' },
      { code: '9000', name: 'Regiearbeit Gärtner', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '9100', name: 'Anfahrt und Wegzeit', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '2000', name: 'Pflanzerde', unit: 'm³', unitPriceRp: 0, kind: 'material' },
      { code: '2010', name: 'Kies und Splitt', unit: 'm³', unitPriceRp: 0, kind: 'material' },
      { code: '2900', name: 'Grüngutentsorgung', unit: 'Psch.', unitPriceRp: 0, kind: 'material' }
    ]
  },
  {
    key: 'montage-service-standard',
    industry: 'montage-service',
    label: 'Montage / Service – Grundkatalog',
    units: ['Std.', 'Stk.', 'lfm', 'Psch.'],
    invoiceConditionsText: ALLGEMEIN_RECHNUNG,
    quoteConditionsText: ALLGEMEIN_OFFERTE,
    items: [
      { code: '1010', name: 'Montage vor Ort', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '1020',
        name: 'Demontage und Entsorgung',
        unit: 'Std.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '1030', name: 'Service und Wartung', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1040', name: 'Störungsbehebung', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '1100',
        name: 'Inbetriebnahme und Einweisung',
        unit: 'Stk.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '9000', name: 'Regiearbeit Monteur', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '9010', name: 'Regiearbeit Lernender', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '9100', name: 'Anfahrt und Wegzeit', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '9200',
        name: 'Notfalleinsatz, Zuschlag',
        unit: 'Psch.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '2900', name: 'Kleinmaterial', unit: 'Psch.', unitPriceRp: 0, kind: 'material' }
    ]
  },
  {
    key: 'bau-regie-standard',
    industry: 'bau-regie',
    label: 'Bau / Regiearbeiten – Grundkatalog',
    units: ['Std.', 'm²', 'm³', 'lfm', 'Stk.', 'Psch.'],
    invoiceConditionsText: ALLGEMEIN_RECHNUNG,
    quoteConditionsText: ALLGEMEIN_OFFERTE,
    items: [
      {
        code: '9000',
        name: 'Regiearbeit Vorarbeiter',
        unit: 'Std.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      {
        code: '9010',
        name: 'Regiearbeit Bauarbeiter',
        unit: 'Std.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '9020', name: 'Regiearbeit Lernender', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '9100', name: 'Anfahrt und Wegzeit', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1010', name: 'Abbruch und Rückbau', unit: 'm²', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1020', name: 'Aushub', unit: 'm³', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1030', name: 'Maurerarbeiten', unit: 'm²', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1040', name: 'Durchbruch erstellen', unit: 'Stk.', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '3000',
        name: 'Maschinenstunde Bagger',
        unit: 'Std.',
        unitPriceRp: 0,
        kind: 'material'
      },
      {
        code: '2900',
        name: 'Mulde und Entsorgung',
        unit: 'Psch.',
        unitPriceRp: 0,
        kind: 'material'
      }
    ]
  },
  {
    key: 'platten-standard',
    industry: 'platten',
    label: 'Plattenleger / Keramik – Grundkatalog',
    units: ['m²', 'lfm', 'Std.', 'Stk.', 'Psch.'],
    invoiceConditionsText: ALLGEMEIN_RECHNUNG,
    quoteConditionsText: ALLGEMEIN_OFFERTE,
    items: [
      { code: '1010', name: 'Bodenplatten verlegen', unit: 'm²', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1020', name: 'Wandplatten verlegen', unit: 'm²', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1030', name: 'Grossformat verlegen', unit: 'm²', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1040', name: 'Mosaik verlegen', unit: 'm²', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '1100',
        name: 'Untergrund vorbereiten und abdichten',
        unit: 'm²',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '1110', name: 'Verfugen', unit: 'm²', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1120', name: 'Silikonfugen setzen', unit: 'lfm', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1200', name: 'Sockel setzen', unit: 'lfm', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '1300',
        name: 'Alte Platten entfernen und entsorgen',
        unit: 'm²',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      {
        code: '9000',
        name: 'Regiearbeit Plattenleger',
        unit: 'Std.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '9100', name: 'Anfahrt und Wegzeit', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '2000', name: 'Plattenkleber', unit: 'Stk.', unitPriceRp: 0, kind: 'material' },
      { code: '2010', name: 'Fugenmasse', unit: 'Stk.', unitPriceRp: 0, kind: 'material' },
      { code: '2900', name: 'Kleinmaterial', unit: 'Psch.', unitPriceRp: 0, kind: 'material' }
    ]
  },
  {
    key: 'bodenbelag-standard',
    industry: 'bodenbelag',
    label: 'Bodenbeläge textil und elastisch – Grundkatalog',
    units: ['m²', 'lfm', 'Std.', 'Stk.', 'Psch.'],
    invoiceConditionsText: ALLGEMEIN_RECHNUNG,
    quoteConditionsText: ALLGEMEIN_OFFERTE,
    items: [
      { code: '1010', name: 'Teppich verlegen', unit: 'm²', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1020', name: 'Designbelag verlegen', unit: 'm²', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1030', name: 'Linoleum verlegen', unit: 'm²', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1040', name: 'Nähte verschweissen', unit: 'lfm', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '1100',
        name: 'Unterlagsboden spachteln und nivellieren',
        unit: 'm²',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      {
        code: '1200',
        name: 'Sockelleisten montieren',
        unit: 'lfm',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      {
        code: '1300',
        name: 'Altbelag entfernen und entsorgen',
        unit: 'm²',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      {
        code: '9000',
        name: 'Regiearbeit Bodenleger',
        unit: 'Std.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '9100', name: 'Anfahrt und Wegzeit', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '2000', name: 'Klebstoff', unit: 'Stk.', unitPriceRp: 0, kind: 'material' },
      { code: '2900', name: 'Kleinmaterial', unit: 'Psch.', unitPriceRp: 0, kind: 'material' }
    ]
  },
  {
    key: 'schreiner-standard',
    industry: 'schreiner',
    label: 'Schreiner / Innenausbau – Grundkatalog',
    units: ['Std.', 'Stk.', 'lfm', 'm²', 'Psch.'],
    invoiceConditionsText: ALLGEMEIN_RECHNUNG,
    quoteConditionsText: ALLGEMEIN_OFFERTE,
    items: [
      {
        code: '1010',
        name: 'Möbel nach Mass fertigen',
        unit: 'Std.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '1020', name: 'Küche montieren', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '1030',
        name: 'Einbauschrank montieren',
        unit: 'lfm',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      {
        code: '1040',
        name: 'Arbeitsplatte zuschneiden und montieren',
        unit: 'lfm',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '1100', name: 'Türe einbauen', unit: 'Stk.', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '1110',
        name: 'Türe einstellen und servicen',
        unit: 'Stk.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '1200', name: 'Aufmass vor Ort', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1300', name: 'Reparatur und Service', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '9000', name: 'Regiearbeit Schreiner', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '9100', name: 'Anfahrt und Wegzeit', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '2000', name: 'Plattenmaterial', unit: 'm²', unitPriceRp: 0, kind: 'material' },
      { code: '2010', name: 'Beschläge', unit: 'Psch.', unitPriceRp: 0, kind: 'material' },
      { code: '2900', name: 'Kleinmaterial', unit: 'Psch.', unitPriceRp: 0, kind: 'material' }
    ]
  },
  {
    key: 'elektro-standard',
    industry: 'elektro',
    label: 'Elektroinstallation – Grundkatalog',
    units: ['Std.', 'Stk.', 'lfm', 'Psch.'],
    invoiceConditionsText: ALLGEMEIN_RECHNUNG,
    quoteConditionsText: ALLGEMEIN_OFFERTE,
    items: [
      { code: '1010', name: 'Steckdose setzen', unit: 'Stk.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1020', name: 'Schalter setzen', unit: 'Stk.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1030', name: 'Leuchte montieren', unit: 'Stk.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1040', name: 'Leitung verlegen', unit: 'lfm', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '1100',
        name: 'Unterverteilung erweitern',
        unit: 'Stk.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      {
        code: '1110',
        name: 'Sicherungsabgang erstellen',
        unit: 'Stk.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      {
        code: '1200',
        name: 'Schlusskontrolle und Sicherheitsnachweis',
        unit: 'Psch.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '1300', name: 'Störungsbehebung', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '9000',
        name: 'Regiearbeit Elektriker',
        unit: 'Std.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '9010', name: 'Regiearbeit Lernender', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '9100', name: 'Anfahrt und Wegzeit', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '2900',
        name: 'Installationsmaterial',
        unit: 'Psch.',
        unitPriceRp: 0,
        kind: 'material'
      }
    ]
  },
  {
    key: 'sanitaer-standard',
    industry: 'sanitaer',
    label: 'Sanitär – Grundkatalog',
    units: ['Std.', 'Stk.', 'lfm', 'Psch.'],
    invoiceConditionsText: ALLGEMEIN_RECHNUNG,
    quoteConditionsText: ALLGEMEIN_OFFERTE,
    items: [
      { code: '1010', name: 'WC montieren', unit: 'Stk.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1020', name: 'Lavabo montieren', unit: 'Stk.', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '1030',
        name: 'Dusche oder Wanne montieren',
        unit: 'Stk.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '1040', name: 'Armatur ersetzen', unit: 'Stk.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1100', name: 'Leitung verlegen', unit: 'lfm', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1110', name: 'Abfluss reinigen', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1120', name: 'Leckortung', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '1200',
        name: 'Notfalleinsatz, Zuschlag',
        unit: 'Psch.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '9000', name: 'Regiearbeit Sanitär', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '9100', name: 'Anfahrt und Wegzeit', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '2900',
        name: 'Installationsmaterial',
        unit: 'Psch.',
        unitPriceRp: 0,
        kind: 'material'
      }
    ]
  },
  {
    key: 'heizung-lueftung-standard',
    industry: 'heizung-lueftung',
    label: 'Heizung / Lüftung – Grundkatalog',
    units: ['Std.', 'Stk.', 'm²', 'lfm', 'Psch.'],
    invoiceConditionsText: ALLGEMEIN_RECHNUNG,
    quoteConditionsText: ALLGEMEIN_OFFERTE,
    items: [
      { code: '1010', name: 'Heizkörper montieren', unit: 'Stk.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1020', name: 'Bodenheizung verlegen', unit: 'm²', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '1030',
        name: 'Wärmepumpe in Betrieb nehmen',
        unit: 'Psch.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      {
        code: '1100',
        name: 'Anlage entlüften und einregulieren',
        unit: 'Std.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '1110', name: 'Serviceeinsatz', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '1200',
        name: 'Lüftungskanal montieren',
        unit: 'lfm',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '1210', name: 'Filterwechsel', unit: 'Stk.', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '9000',
        name: 'Regiearbeit Heizungsmonteur',
        unit: 'Std.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '9100', name: 'Anfahrt und Wegzeit', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '2900', name: 'Material', unit: 'Psch.', unitPriceRp: 0, kind: 'material' }
    ]
  },
  {
    key: 'dach-spengler-standard',
    industry: 'dach-spengler',
    label: 'Dach / Spengler – Grundkatalog',
    units: ['m²', 'lfm', 'Std.', 'Stk.', 'Psch.'],
    invoiceConditionsText: ALLGEMEIN_RECHNUNG,
    quoteConditionsText: ALLGEMEIN_OFFERTE,
    items: [
      { code: '1010', name: 'Ziegel ersetzen', unit: 'm²', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1020', name: 'Unterdach sanieren', unit: 'm²', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1030', name: 'Flachdach abdichten', unit: 'm²', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1100', name: 'Dachrinne montieren', unit: 'lfm', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1110', name: 'Ablaufrohr montieren', unit: 'lfm', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '1120',
        name: 'Blechanschluss erstellen',
        unit: 'lfm',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '1200', name: 'Dachfenster einbauen', unit: 'Stk.', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '1300',
        name: 'Gerüst stellen und abbauen',
        unit: 'Psch.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      {
        code: '9000',
        name: 'Regiearbeit Dachdecker',
        unit: 'Std.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '9100', name: 'Anfahrt und Wegzeit', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '2900', name: 'Material', unit: 'Psch.', unitPriceRp: 0, kind: 'material' }
    ]
  },
  {
    key: 'fenster-storen-standard',
    industry: 'fenster-storen',
    label: 'Fenster / Storen – Grundkatalog',
    units: ['Stk.', 'lfm', 'Std.', 'Psch.'],
    invoiceConditionsText: ALLGEMEIN_RECHNUNG,
    quoteConditionsText: ALLGEMEIN_OFFERTE,
    items: [
      { code: '1010', name: 'Fenster ersetzen', unit: 'Stk.', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '1020',
        name: 'Fenster einstellen und servicen',
        unit: 'Stk.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '1030', name: 'Fensterbank montieren', unit: 'lfm', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1100', name: 'Storen montieren', unit: 'Stk.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1110', name: 'Storenmotor ersetzen', unit: 'Stk.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1120', name: 'Lamellen ersetzen', unit: 'Stk.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1130', name: 'Rollladen reparieren', unit: 'Stk.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '9000', name: 'Regiearbeit Monteur', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '9100', name: 'Anfahrt und Wegzeit', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '2900', name: 'Material', unit: 'Psch.', unitPriceRp: 0, kind: 'material' }
    ]
  },
  {
    key: 'umzug-transport-standard',
    industry: 'umzug-transport',
    label: 'Umzug / Transport – Grundkatalog',
    units: ['Std.', 'Stk.', 'Psch.'],
    invoiceConditionsText: ALLGEMEIN_RECHNUNG,
    quoteConditionsText: ALLGEMEIN_OFFERTE,
    items: [
      { code: '1010', name: 'Umzug mit Team', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1020', name: 'Ein- und Auspacken', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      {
        code: '1030',
        name: 'Möbel demontieren und montieren',
        unit: 'Std.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '1100', name: 'Möbellift stellen', unit: 'Psch.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1110', name: 'Entsorgungsfahrt', unit: 'Psch.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '1200', name: 'Lagerung pro Monat', unit: 'Psch.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '9000', name: 'Regiearbeit Zügelmann', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '9100', name: 'Anfahrt und Wegzeit', unit: 'Std.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '2900', name: 'Umzugsmaterial', unit: 'Psch.', unitPriceRp: 0, kind: 'material' }
    ]
  },
  /*
    ══════════════════════════════════════════════════════════════════════════
    Kinderbetreuung – was dieses Paket ist und was es ausdrücklich nicht ist
    ══════════════════════════════════════════════════════════════════════════

    Es ist eine Liste von Leistungen OHNE Preise. Jede Kita rechnet anders:
    Monatspauschalen, Halbtage mit und ohne Mittagessen, Zusatztage,
    Eingewöhnung, Geschwisterrabatte – die Posten sind überall dieselben, die
    Beträge nirgends. Ein erfundener Tarif wäre hier schlimmer als in jedem
    Handwerk, weil Eltern ihn als Zusage lesen.

    Es enthält KEINE Daten eines einzelnen Betriebs. Was ein konkreter Betrieb
    verlangt, steht in seinem eigenen Katalog (`catalog_items`) und nirgends
    sonst; dieses Modul wird jedem Mandanten ausgeliefert.

    ──────────────────────────────────────────────────────────────────────────
    Die Einheit «Tag/Woche» – die einzige Stelle, die Erklärung braucht
    ──────────────────────────────────────────────────────────────────────────

    Die drei Pauschalen werden pro BETREUUNGSTAG PRO WOCHE und Monat
    verrechnet. Ein Kind mit zwei Ganztagen kostet zweimal den Satz, eines mit
    zweieinhalb zweieinhalbmal – die veröffentlichten Tarifstufen sind genau
    diese Multiplikation. Damit genügt EIN Preis je Leistung statt fünf
    Mengenstufen, Zwischenwerte wie 0.75 oder 2.5 ergeben sich von selbst, und
    eine Tariferhöhung ist eine Zahl und keine Tabelle.

    Die Monatspauschale bleibt dabei eine Monatspauschale: Sie hängt nicht an
    der Zahl der Kalendertage und wird zwölfmal im Jahr verrechnet.

    ──────────────────────────────────────────────────────────────────────────
    Baby und Kleinkind sind Preisvarianten, keine eigenen Leistungen
    ──────────────────────────────────────────────────────────────────────────

    Viele Kitas führen zwei Tarife nach Alter, manche nur einen. Deshalb steht
    jede Leistung hier EINMAL. Ein Betrieb mit Alterstarifen legt in seinem
    Katalog zwei Zeilen daneben und hängt an den Code ein Kürzel:

        1010     ohne Altersunterscheidung
        1010-B   Babytarif
        1010-K   Kleinkindtarif

    Die Abrechnung sucht zuerst das Kürzel und fällt auf den Grundcode zurück
    (siehe `services/kita.ts`). Eine Kita ohne Alterstarife merkt von der Regel
    nichts, und keine Branche wird dafür verdoppelt.
  */
  {
    key: 'kinderbetreuung-standard',
    industry: 'kinderbetreuung',
    label: 'Kinderbetreuung – Grundkatalog',
    units: ['Tag/Woche', 'Tag', 'Psch.', 'Stk.', 'Monat'],
    invoiceConditionsText: ALLGEMEIN_KITA_RECHNUNG,
    quoteConditionsText: ALLGEMEIN_OFFERTE,
    items: [
      /* Die Pauschalen – Preis je Betreuungstag pro Woche und Monat. */
      {
        code: '1010',
        name: 'Ganztagesbetreuung',
        unit: 'Tag/Woche',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      {
        code: '1020',
        name: 'Halbtagesbetreuung ohne Mittagessen',
        unit: 'Tag/Woche',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      {
        code: '1030',
        name: 'Halbtagesbetreuung mit Mittagessen',
        unit: 'Tag/Woche',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      /* Zusatzbetreuung – je Einzeltermin und nicht pro Woche. */
      {
        code: '1110',
        name: 'Zusätzlicher ganzer Betreuungstag',
        unit: 'Tag',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      {
        code: '1120',
        name: 'Zusätzlicher halber Betreuungstag ohne Mittagessen',
        unit: 'Tag',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      {
        code: '1130',
        name: 'Zusätzlicher halber Betreuungstag mit Mittagessen',
        unit: 'Tag',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '1200', name: 'Eingewöhnung', unit: 'Psch.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '2010', name: 'Mittagessen', unit: 'Stk.', unitPriceRp: 0, kind: 'material' },
      /*
        Der Geschwisterrabatt ist eine Leistung mit positivem Preis und wird
        auf dem Beleg als Abzug geführt. Ein negativer Katalogpreis wäre die
        erste Zahl, die beim Tippen ein Vorzeichen verliert.
      */
      {
        code: '8010',
        name: 'Geschwisterrabatt',
        unit: 'Tag',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      /* Für vereinbarte Modelle, die in keine Stufe passen. */
      {
        code: '9010',
        name: 'Individuelle Betreuungsleistung',
        unit: 'Psch.',
        unitPriceRp: 0,
        kind: 'arbeit'
      },
      { code: '9020', name: 'Betreuungszuschlag', unit: 'Psch.', unitPriceRp: 0, kind: 'arbeit' },
      { code: '9030', name: 'Betreuungsgutschrift', unit: 'Psch.', unitPriceRp: 0, kind: 'arbeit' }
    ]
  }
]

/*
  Hier stand ein zweiter Eintrag mit dem Namen eines konkreten Betriebs darin.

  Ein Mandant, der seinen Bestand aus einer Altsoftware übernimmt, braucht kein
  Startpaket – er hat ja schon Inhalt. Was bleibt, ist die Herkunft, und die
  gehört als Schlüssel in tenant_settings.start_package_key, nicht als Zeile in
  ein Modul, das jeder Betrieb ausgeliefert bekommt. startPackage() gibt für
  einen solchen Schlüssel null zurück; das ist der erwartete Fall und kein
  Fehler.
*/

export function startPackage(key: string): StartPackage | null {
  return START_PACKAGES.find((p) => p.key === key) ?? null
}

export function packagesFor(industry: Industry): StartPackage[] {
  return START_PACKAGES.filter((p) => p.industry === industry)
}

/**
 * Die Standardtexte.
 *
 * Sie stehen hier und nicht verstreut in Migration, Seed und Formular – drei
 * Kopien desselben Satzes weichen mit Sicherheit irgendwann voneinander ab.
 *
 * Zur Formulierung: «Zahlbar innert 30 Tagen ab Rechnungsdatum» nennt Frist UND
 * Bezugspunkt. «30 Tage netto» allein lässt offen, ab wann gerechnet wird, und
 * genau darüber entsteht später die Diskussion mit dem Kunden.
 */
export const STANDARD_RECHNUNGSTEXT = 'Zahlbar innert 30 Tagen ab Rechnungsdatum.'

export const STANDARD_RECHNUNGSTEXT_LANG =
  'Zahlbar innert 30 Tagen ab Rechnungsdatum. Bei Fragen zur Rechnung ' +
  'kontaktieren Sie uns bitte vor Ablauf der Zahlungsfrist.'

export const STANDARD_OFFERTTEXT = 'Diese Offerte ist 30 Tage ab Ausstellungsdatum gültig.'

/**
 * Anrede und Abschluss auf dem Beleg.
 *
 * Bisher begann das Blatt mit der Tabelle. Zwischen Adressblock und erster
 * Position stand nichts – kein Satz, der sagt, worum es geht und warum dieses
 * Papier im Briefkasten liegt. Das liest sich wie ein Kontoauszug, nicht wie
 * ein Schreiben eines Betriebs, den man kennt.
 *
 * Diese vier Sätze sind der Vorgabewert, nicht die Vorschrift: sobald in den
 * Einstellungen eigene Texte hinterlegt sind, treten sie an ihre Stelle. Sie
 * stehen hier bei den anderen Standardtexten, damit es nicht wieder drei
 * Kopien desselben Satzes gibt.
 *
 * Kein Betrag, keine Frist, keine Zahl darin – das steht in den Positionen
 * und in den Konditionen, und dort wird es auch gepflegt.
 */
export const STANDARD_RECHNUNG_EINLEITUNG =
  'Vielen Dank für Ihren Auftrag. Wir stellen Ihnen folgende Leistungen in Rechnung.'

export const STANDARD_RECHNUNG_SCHLUSS = 'Besten Dank für die angenehme Zusammenarbeit.'

/**
 * Der Rechnungstext einer Kita.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum dieser Satz einen eigenen braucht
 * ────────────────────────────────────────────────────────────────────────────
 *
 * «Vielen Dank für Ihren Auftrag. Wir stellen Ihnen folgende Leistungen in
 * Rechnung» passt zu einem Handwerker, der eine Bestellung ausgeführt hat.
 * Eltern haben keinen Auftrag erteilt, sie haben ihr Kind angemeldet – und
 * die Monatsrechnung ist keine Abrechnung eines Auftrags, sondern die
 * vereinbarte Betreuung eines Zeitraums.
 *
 * Der Satz nennt keine Beträge, keine Fristen und keinen Namen: Das steht in
 * den Positionen und in den Konditionen, und dort wird es gepflegt.
 *
 * Es ist ein VORGABEWERT wie die anderen. Hat ein Betrieb einen eigenen Text
 * hinterlegt, gilt dieser – auch eine Kita.
 */
export const STANDARD_RECHNUNG_EINLEITUNG_KITA =
  'Wir stellen Ihnen die vereinbarten Betreuungsleistungen für den angegebenen ' +
  'Zeitraum in Rechnung. Vielen Dank für Ihr Vertrauen.'

export const STANDARD_OFFERTE_EINLEITUNG =
  'Vielen Dank für Ihre Anfrage. Gerne unterbreiten wir Ihnen folgende Offerte.'

export const STANDARD_OFFERTE_SCHLUSS =
  'Wir freuen uns auf Ihre Rückmeldung und stehen bei Fragen gerne zur Verfügung.'
